import { html } from 'lit';
import type {
  WebEditorElementFrame,
  WebEditorLayoutProject,
  WebEditorProjectSettings,
} from '../../layout.js';
import {
  automaticMobileFontSize,
  clampTypographySize,
  typographyStyleDefinitions,
  typographyStyleSizes,
  type WebEditorTypographyScale,
  type TypographyStyleKey,
  type TypographyViewport,
} from '../../typography-scale.js';
import type { WebEditorElementDefinition } from '../../element-definition.js';

interface TypographyControlsOptions {
  definition?: WebEditorElementDefinition;
  elementId: string;
  frame: WebEditorElementFrame;
  project: WebEditorLayoutProject;
  updateFrame: (elementId: string, update: Partial<WebEditorElementFrame>) => void;
  updateSettings: (update: Partial<WebEditorProjectSettings>) => void;
}

const inputSize = (event: Event): number | undefined => {
  const value = (event.target as HTMLInputElement).value.trim();
  const numericValue = Number(value);
  return value && Number.isFinite(numericValue) ? clampTypographySize(numericValue) : undefined;
};

const updateGlobalSize = (
  options: TypographyControlsOptions,
  style: TypographyStyleKey,
  viewport: TypographyViewport,
  value: number | undefined,
) => {
  const typography: WebEditorTypographyScale = { ...(options.project.settings?.typography ?? {}) };
  const entry = { ...(typography[style] ?? {}) };
  if (value === undefined) delete entry[viewport];
  else entry[viewport] = value;
  if (entry.desktop === undefined && entry.mobile === undefined) delete typography[style];
  else typography[style] = entry;
  options.updateSettings({ typography });
};

const globalSizeControl = (
  options: TypographyControlsOptions,
  style: TypographyStyleKey,
  viewport: TypographyViewport,
) => {
  const configured = options.project.settings?.typography?.[style]?.[viewport];
  const sizes = typographyStyleSizes(options.project.settings?.typography, style);
  const value = viewport === 'desktop' ? sizes.desktop : sizes.mobile;
  return html`
    <label>
      ${viewport === 'desktop' ? 'Global Desktop' : 'Global Mobile'}
      <div class="vp-editor-type-input-row">
        <input
          type="number"
          min="8"
          max="240"
          step="1"
          .value=${String(value)}
          @change=${(event: Event) => updateGlobalSize(options, style, viewport, inputSize(event))}
          @keydown=${(event: KeyboardEvent) => {
            if (event.key === 'Enter') (event.currentTarget as HTMLInputElement).blur();
          }}
        />
        <span>px</span>
      </div>
      <div class="vp-editor-type-meta">
        <small
          >${configured === undefined
            ? viewport === 'mobile'
              ? 'Automatisch'
              : 'Standardwert'
            : 'Global angepasst'}</small
        >
        ${configured === undefined
          ? ''
          : html`
              <button
                type="button"
                class="vp-editor-type-reset"
                @click=${() => updateGlobalSize(options, style, viewport, undefined)}
              >
                Zurücksetzen
              </button>
            `}
      </div>
    </label>
  `;
};

const localSizeControl = (
  options: TypographyControlsOptions,
  viewport: TypographyViewport,
  placeholder: string,
  automaticLabel: string,
) => {
  const property = viewport === 'desktop' ? 'fontSizeDesktop' : 'fontSizeMobile';
  const value = options.frame[property];
  return html`
    <label class="vp-editor-type-field">
      <span>${viewport === 'desktop' ? 'Desktop' : 'Mobile'}</span>
      <div class="vp-editor-type-number-field ${value === undefined ? '' : 'is-overridden'}">
        <span
          class=${viewport === 'desktop'
            ? 'icon-[material-symbols--desktop-windows-outline]'
            : 'icon-[material-symbols--smartphone]'}
          aria-hidden="true"
        ></span>
        <input
          type="number"
          min="8"
          max="240"
          step="1"
          .value=${value === undefined ? '' : String(value)}
          placeholder=${placeholder}
          aria-label=${`Schriftgröße ${viewport === 'desktop' ? 'Desktop' : 'Mobile'}`}
          @change=${(event: Event) => options.updateFrame(options.elementId, { [property]: inputSize(event) })}
          @keydown=${(event: KeyboardEvent) => {
            if (event.key === 'Enter') (event.currentTarget as HTMLInputElement).blur();
          }}
        />
        <span class="vp-editor-type-unit">px</span>
        ${value === undefined
          ? ''
          : html`
              <button
                type="button"
                class="vp-editor-type-clear"
                title="Lokale Größe zurücksetzen"
                aria-label="Lokale Größe zurücksetzen"
                @click=${() => options.updateFrame(options.elementId, { [property]: undefined })}
              >
                <span class="icon-[material-symbols--restart-alt]" aria-hidden="true"></span>
              </button>
            `}
      </div>
      <small>${value === undefined ? automaticLabel : 'Lokal'}</small>
    </label>
  `;
};

const closeStylePopover = (event: Event) => {
  const popover = (event.currentTarget as HTMLElement).closest('[popover]') as
    | (HTMLElement & { hidePopover?: () => void })
    | null;
  popover?.hidePopover?.();
};

export const renderTypographyProperties = (options: TypographyControlsOptions) => {
  if (options.definition?.typographyMode === 'none') return '';
  const style = options.frame.typographyStyle;
  const styleDefinition = typographyStyleDefinitions.find(definition => definition.key === style);
  const styleSizes = style ? typographyStyleSizes(options.project.settings?.typography, style) : undefined;
  const automaticMobile =
    options.frame.fontSizeDesktop !== undefined
      ? automaticMobileFontSize(options.frame.fontSizeDesktop, style)
      : styleSizes?.mobile;
  const h1Count = Object.values(options.project.elementFrames).filter(frame => frame.typographyStyle === 'h1').length;
  const activeAlign = options.frame.textAlign ?? 'left';
  const popoverId = `vp-editor-type-style-${options.elementId.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  const setStyle = (event: Event, nextStyle?: TypographyStyleKey) => {
    options.updateFrame(options.elementId, {
      typographyStyle: nextStyle,
      fontSizeDesktop: undefined,
      fontSizeMobile: undefined,
    });
    closeStylePopover(event);
  };

  return html`
    <div class="vp-editor-type-inspector">
      <div class="vp-editor-type-header">
        <div>
          <strong>Typografie</strong>
          <span>${styleDefinition?.label ?? 'Seitendesign'}${style ? ' · global' : ''}</span>
        </div>
        <button
          type="button"
          class="vp-editor-type-settings-trigger ${style ? 'is-active' : ''}"
          popovertarget=${popoverId}
          title="Schriftformat und globale Werte"
          aria-label="Schriftformat und globale Werte öffnen"
        >
          <span class="icon-[material-symbols--settings-outline]" aria-hidden="true"></span>
        </button>
      </div>

      <div class="vp-editor-type-primary-grid">
        <label class="vp-editor-type-field vp-editor-type-weight">
          <span>Stärke</span>
          <select
            .value=${options.frame.fontWeight === undefined ? '' : String(options.frame.fontWeight)}
            @change=${(event: Event) => {
              const value = (event.target as HTMLSelectElement).value;
              options.updateFrame(options.elementId, { fontWeight: value ? Number(value) : undefined });
            }}
          >
            <option value="">Standard</option>
            <option value="400">Regular</option>
            <option value="600">Semibold</option>
            <option value="800">Bold</option>
            <option value="900">Black</option>
          </select>
          <small>${options.frame.fontWeight === undefined ? 'Seitendesign' : 'Lokal'}</small>
        </label>
        ${localSizeControl(
          options,
          'desktop',
          styleSizes ? String(styleSizes.desktop) : 'CSS',
          style ? 'Global' : 'CSS',
        )}
        ${localSizeControl(
          options,
          'mobile',
          automaticMobile === undefined ? 'Auto' : String(automaticMobile),
          'Automatisch',
        )}
      </div>

      <div class="vp-editor-type-alignment-row">
        <span>Ausrichtung</span>
        <div class="vp-editor-type-segmented" role="group" aria-label="Textausrichtung">
          ${(['left', 'center', 'right'] as const).map(
            align => html`
              <button
                type="button"
                class=${activeAlign === align ? 'is-active' : ''}
                title=${align === 'left' ? 'Linksbündig' : align === 'center' ? 'Zentriert' : 'Rechtsbündig'}
                aria-label=${align === 'left' ? 'Linksbündig' : align === 'center' ? 'Zentriert' : 'Rechtsbündig'}
                @click=${() => options.updateFrame(options.elementId, { textAlign: align })}
              >
                <span
                  class=${align === 'left'
                    ? 'icon-[material-symbols--format-align-left]'
                    : align === 'center'
                      ? 'icon-[material-symbols--format-align-center]'
                      : 'icon-[material-symbols--format-align-right]'}
                  aria-hidden="true"
                ></span>
              </button>
            `,
          )}
        </div>
      </div>

      ${style === 'h1' && h1Count > 1
        ? html`
            <p class="vp-editor-type-notice" role="status">
              SEO-Hinweis: Im Projekt sind ${h1Count} Texte als H1 formatiert. Das ist erlaubt, sollte aber bewusst
              gewählt sein.
            </p>
          `
        : ''}

      <div id=${popoverId} class="vp-editor-type-popover" popover>
        <header>
          <div>
            <strong>Schriftformat</strong>
            <span>Stil für dieses Element</span>
          </div>
          <button
            type="button"
            class="vp-editor-type-popover-close"
            popovertarget=${popoverId}
            popovertargetaction="hide"
            aria-label="Schließen"
          >
            <span class="icon-[material-symbols--close]" aria-hidden="true"></span>
          </button>
        </header>

        <section class="vp-editor-type-style-section">
          <button
            type="button"
            class="vp-editor-type-site-style ${style ? '' : 'is-active'}"
            @click=${(event: Event) => setStyle(event)}
          >
            <span class="icon-[material-symbols--web]" aria-hidden="true"></span>
            <span><strong>Bestehendes Seitendesign</strong><small>Keine globale Zuweisung</small></span>
            ${style ? '' : html`<span class="icon-[material-symbols--check]" aria-hidden="true"></span>`}
          </button>

          ${(
            [
              ['Überschriften', typographyStyleDefinitions.slice(0, 6)],
              ['Absätze', typographyStyleDefinitions.slice(6, 9)],
              ['UI-Texte', typographyStyleDefinitions.slice(9)],
            ] as const
          ).map(
            ([label, definitions]) => html`
              <div class="vp-editor-type-style-group">
                <span>${label}</span>
                <div>
                  ${definitions.map(
                    definition => html`
                      <button
                        type="button"
                        class=${style === definition.key ? 'is-active' : ''}
                        @click=${(event: Event) => setStyle(event, definition.key)}
                      >
                        ${definition.label}
                      </button>
                    `,
                  )}
                </div>
              </div>
            `,
          )}
        </section>

        ${style
          ? html`
              <section class="vp-editor-type-global-section">
                <div>
                  <strong>${styleDefinition?.label} global</strong>
                  <span>Ändert alle Texte mit diesem Stil</span>
                </div>
                <div class="vp-editor-type-size-grid">
                  ${globalSizeControl(options, style, 'desktop')} ${globalSizeControl(options, style, 'mobile')}
                </div>
              </section>
            `
          : ''}
        ${options.definition?.typographyMode === 'visual' && style
          ? html`
              <p class="vp-editor-type-hint">
                Dieses Element behält sein gültiges HTML-Element; der gewählte Stil wirkt visuell.
              </p>
            `
          : ''}
      </div>
    </div>
  `;
};

export const renderGlobalTypographySettings = (
  project: WebEditorLayoutProject,
  updateSettings: (update: Partial<WebEditorProjectSettings>) => void,
) => {
  const baseOptions: TypographyControlsOptions = {
    elementId: '',
    frame: { x: 0, y: 0 },
    project,
    updateFrame: () => undefined,
    updateSettings,
  };
  return html`
    <section class="vp-editor-settings-section">
      <h3>Globale Typografie</h3>
      <p class="vp-editor-muted">
        Desktop und Mobile gelten websiteweit für alle Texte mit dem jeweiligen Schriftformat. Leeres Mobile-Feld bleibt
        automatisch gekoppelt.
      </p>
      <div class="vp-editor-type-settings-list">
        ${typographyStyleDefinitions.map(
          definition => html`
            <div class="vp-editor-type-settings-row">
              <strong>${definition.label}</strong>
              ${globalSizeControl(baseOptions, definition.key, 'desktop')}
              ${globalSizeControl(baseOptions, definition.key, 'mobile')}
            </div>
          `,
        )}
      </div>
    </section>
  `;
};
